import {zodResolver} from '@hookform/resolvers/zod';
import {createContext, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {DefaultValues, FieldValues, UseFormReturn, useForm} from 'react-hook-form';

import {
  FormFieldT,
  FormFieldTypeT,
  FormStateT,
  OnChangePropsT,
  WatchConfigT,
  WatchEffectPropsT,
} from '@/lib/types/form.types';

/* ******************************************************************
 * Types
 ****************************************************************** */
export type FormHookReturnT<T extends FieldValues> = {
  formConfig: UseFormReturn<T>;
  state: FormStateT<T>;
  setGlobalError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  getStepFields: () => FormFieldT<T>[];
  validateStep: (step: number) => Promise<boolean>;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  resetForm: () => void;
};

export type AnyFormStateT = FormStateT<FieldValues>;

/* ******************************************************************
 * Context
 ****************************************************************** */
export type AppFormContextT = {
  // -------------- ----------------------------------------------
  // Form State Management
  // ------------------------------------------------------------
  getFormState: <T extends FieldValues>(formId: string) => FormStateT<T>;
  setFormState: <T extends FieldValues>(formId: string, state: Partial<FormStateT<T>>) => void;

  // -------------- ----------------------------------------------
  // Step Management
  // ------------------------------------------------------------
  validateStep: (formId: string, step: number) => Promise<boolean>;
  nextStep: (formId: string) => void;
  prevStep: (formId: string) => void;
  goToStep: (formId: string, step: number) => void;

  // -------------- ----------------------------------------------
  // Form Status Management
  // ------------------------------------------------------------
  setGlobalError: (formId: string, error: string | null) => void;
  setLoading: (formId: string, loading: boolean) => void;

  // -------------- ----------------------------------------------
  // Form Lifecycle Management
  // ------------------------------------------------------------
  initForm: <T extends FieldValues>(formId: string, config: Partial<FormStateT<T>>) => void;
  resetForm: (formId: string) => void;

  // -------------- ----------------------------------------------
  // Form Fields Management
  // ------------------------------------------------------------
  getStepFields: <T extends FieldValues>(formId: string) => FormFieldT<T>[];
};

const AppFormContext = createContext<AppFormContextT>({} as AppFormContextT);

/* ******************************************************************
 * Provider
 ****************************************************************** */
export const AppFormProvider = ({children}: {children: React.ReactNode}) => {
  // -------------- ----------------------------------------------
  // State
  // ------------------------------------------------------------
  const [formStates, setFormStates] = useState<Record<string, AnyFormStateT>>({});

  // -------------- ----------------------------------------------
  // Form State Management
  // ------------------------------------------------------------
  const getFormState = <T extends FieldValues>(formId: string): FormStateT<T> => {
    return (formStates[formId] || {step: 0, loading: false, globalError: null, initialized: false}) as FormStateT<T>;
  };

  const setFormState = <T extends FieldValues>(formId: string, state: Partial<FormStateT<T>>) => {
    setFormStates(prev => ({
      ...prev,
      [formId]: {...(prev[formId] || {}), ...state} as AnyFormStateT,
    }));
  };

  // -------------- ----------------------------------------------
  // Form Fields Management
  // ------------------------------------------------------------
  const getStepFields = <T extends FieldValues>(formId: string): FormFieldT<T>[] => {
    const state = formStates[formId];
    if (!state?.steps || !state.fields) return [];

    const currentStep = state.step || 0;
    const stepFields = state.steps[currentStep]?.fields || [];

    // When fields for a step are requested, sync any watch configs that involve these fields
    const fieldsWithConfigs = state.fields.filter(field => {
      const hasConfigs = (field.watch?.configs?.length && field.watch.enabled) || field.watchConfig;
      const isInCurrentStep = stepFields.includes(field.name);
      return hasConfigs || isInCurrentStep;
    });

    // Process all watch configs
    fieldsWithConfigs.forEach(field => {
      const configs = field.watch?.configs || [];
      configs.forEach(config => {
        // If either the source or target field is in this step, sync the values
        if (stepFields.includes(config.source) || (config.target && stepFields.includes(config.target))) {
          const values = state.formConfig?.getValues() || {};
          const sourceValue = values[config.source];

          if (sourceValue !== undefined && config.target) {
            state.formConfig?.setValue(config.target, sourceValue, {
              shouldValidate: false,
              shouldDirty: true,
            });
          }
        }
      });
    });

    return state.fields
      .filter(field => stepFields.includes(field.name))
      .map(field => ({
        ...field,
        watch: field.watch
          ? {
              ...field.watch,
              onChange: ({value, formContext, field: fieldProps, setWatchEnabled}: OnChangePropsT<FormFieldTypeT>) => {
                // -------------- ----------------------------------------------
                // Call original onChange if it exists
                // ------------------------------------------------------------
                field.watch?.onChange?.({value, formContext, field: fieldProps, setWatchEnabled});

                // -------------- ----------------------------------------------
                // If watch is being enabled, sync all values
                // ------------------------------------------------------------
                if (typeof value === 'boolean' && value === true) {
                  const values = state.formConfig?.getValues() || {};
                  field.watch?.configs?.forEach(config => {
                    const sourceValue = values[config.source];
                    if (sourceValue !== undefined && config.target) {
                      state.formConfig?.setValue(config.target, sourceValue, {
                        shouldValidate: false,
                        shouldDirty: true,
                      });
                    }
                  });
                }
              },
            }
          : undefined,
        key: `${field.name}_${currentStep}`,
      })) as FormFieldT<T>[];
  };

  // -------------- ----------------------------------------------
  // Step Management
  // ------------------------------------------------------------
  const validateStep = async (formId: string, step: number): Promise<boolean> => {
    const state = formStates[formId];
    if (!state?.steps || !state.formConfig) return false;
    if (state.validateSteps === false) return true;

    const formConfig = state.formConfig;
    const stepFields = state.steps[step]?.fields || [];
    let firstInvalidField: string | null = null;

    // Process watch configurations for the step
    const fieldsWithConfigs = state.fields?.filter(field => {
      const hasConfigs = field.watch?.configs?.length || field.watchConfig;
      const isInCurrentStep = stepFields.includes(field.name);
      return hasConfigs && isInCurrentStep;
    });

    // Apply watch configurations before validation
    fieldsWithConfigs?.forEach(field => {
      if (field.watchConfig) {
        // Handle copyFrom configuration
        if (field.watchConfig.copyFrom) {
          const sourceField =
            typeof field.watchConfig.copyFrom === 'string'
              ? field.watchConfig.copyFrom
              : field.watchConfig.copyFrom.source;
          const sourceValue = formConfig.getValues(sourceField);

          // Only copy if the field should be disabled (meaning "Same as" is checked)
          if (field.watchConfig.disableWhen) {
            const disableField = field.watchConfig.disableWhen.field;
            const disableValue = formConfig.getValues(disableField);

            if (disableValue === field.watchConfig.disableWhen.is && sourceValue !== undefined) {
              formConfig.setValue(field.name, sourceValue, {
                shouldValidate: true,
                shouldTouch: true,
                shouldDirty: true,
              });
            }
          }
        }
      }

      // Handle legacy watch configs
      const configs = field.watch?.configs || [];
      configs.forEach(config => {
        if (config.autoCopy && 'condition' in config) {
          const condition = (config as {condition?: {field: string; is: boolean}}).condition;
          if (condition) {
            const conditionValue = formConfig.getValues(condition.field);
            if (conditionValue === condition.is) {
              const sourceValue = formConfig.getValues(config.source);
              if (sourceValue !== undefined && config.target) {
                formConfig.setValue(config.target, sourceValue, {
                  shouldValidate: true,
                  shouldTouch: true,
                  shouldDirty: true,
                });
              }
            }
          }
        }
      });
    });

    // Mark all fields as touched and trigger validation
    for (const field of stepFields) {
      // Get current field value and metadata
      const fieldValue = formConfig.getValues(field);
      const fieldType = state.fields?.find(f => f.name === field)?.type;
      const defaultValue = fieldType === 'checkbox' ? false : formConfig?.formState.defaultValues?.[field];
      const finalValue = fieldValue ?? defaultValue;
      const fieldDef = state.fields?.find(f => f.name === field);

      // Check if field should be disabled and has a copyFrom config
      const isDisabled =
        fieldDef?.watchConfig?.disableWhen &&
        formConfig.getValues(fieldDef.watchConfig.disableWhen.field) === fieldDef.watchConfig.disableWhen.is;
      const hasCopyFrom = fieldDef?.watchConfig?.copyFrom;

      // If field is disabled and has copyFrom config, get the source value
      if (isDisabled && hasCopyFrom) {
        const sourceField = typeof hasCopyFrom === 'string' ? hasCopyFrom : hasCopyFrom.source;
        const sourceValue = formConfig.getValues(sourceField);

        // Use the source value for validation
        if (sourceValue !== undefined) {
          formConfig.setValue(field, sourceValue, {
            shouldValidate: true,
            shouldTouch: true,
            shouldDirty: true,
          });
          continue;
        }
      }

      // Skip validation for fields that should be disabled and have a copyFrom config
      if (isDisabled && hasCopyFrom) {
        continue;
      }

      // Skip validation for static fields
      if (fieldDef?.type === 'static') {
        continue;
      }

      // Check if field is required and empty
      if (
        fieldDef?.options?.required &&
        !isDisabled &&
        (finalValue === undefined || finalValue === '' || finalValue === null)
      ) {
        formConfig.setError(field, {
          type: 'required',
          message: typeof fieldDef.options.required === 'string' ? fieldDef.options.required : 'This field is required',
        });
        if (!firstInvalidField) firstInvalidField = field;
        continue;
      }

      // Only set value and validate if the field is not disabled
      if (!isDisabled) {
        formConfig.setValue(field, finalValue, {
          shouldValidate: true,
          shouldTouch: true,
          shouldDirty: true,
        });

        // Trigger field-specific validation
        const isFieldValid = await formConfig.trigger(field);

        if (!isFieldValid && !firstInvalidField) {
          firstInvalidField = field;
        }
      }
    }

    if (firstInvalidField) {
      formConfig.setFocus(firstInvalidField);
      return false;
    }

    return true;
  };

  const nextStep = async (formId: string) => {
    const state = formStates[formId];
    if (!state?.steps) return;

    const currentStep = state.step || 0;
    // Validate all fields in current step
    const isValid = await validateStep(formId, currentStep);

    if (isValid) {
      setFormStates(prev => ({
        ...prev,
        [formId]: {...prev[formId], step: currentStep + 1},
      }));
    }
  };

  const goToStep = async (formId: string, targetStep: number) => {
    const state = formStates[formId];
    if (!state?.steps || !state.formConfig) return;

    if (state.validateSteps === false) {
      setFormStates(prev => ({
        ...prev,
        [formId]: {...prev[formId], step: targetStep},
      }));
      return;
    }

    const currentStep = state.step || 0;

    // If trying to go forward, validate all steps up to the target
    if (targetStep > currentStep) {
      for (let i = 0; i < targetStep; i++) {
        const isValid = await validateStep(formId, i);

        if (!isValid) {
          setFormStates(prev => ({
            ...prev,
            [formId]: {...prev[formId], step: i},
          }));
          return;
        }
      }
    }

    setFormStates(prev => ({
      ...prev,
      [formId]: {...prev[formId], step: targetStep},
    }));
  };

  const prevStep = (formId: string) => {
    // No validation needed when going back
    setFormStates(prev => ({
      ...prev,
      [formId]: {...prev[formId], step: Math.max(0, (prev[formId]?.step || 0) - 1)},
    }));
  };

  // -------------- ----------------------------------------------
  // Form Status Management
  // ------------------------------------------------------------
  const setGlobalError = (formId: string, error: string | null) => {
    setFormStates(prev => ({
      ...prev,
      [formId]: {...prev[formId], globalError: error},
    }));
  };

  const setLoading = (formId: string, loading: boolean) => {
    setFormStates(prev => ({
      ...prev,
      [formId]: {...prev[formId], loading},
    }));
  };

  // -------------- ----------------------------------------------
  // Form Lifecycle Management
  // ------------------------------------------------------------
  const initForm = <T extends FieldValues>(formId: string, config: Partial<FormStateT<T>>) => {
    setFormStates(prev => ({
      ...prev,
      [formId]: {
        step: config.step || 0,
        loading: config.loading || false,
        globalError: config.globalError || null,
        defaultValues: config.defaultValues,
        steps: config.steps,
        // Process any fields with watchConfig
        fields: config.fields?.map(field => (field?.watchConfig ? processWatchConfig(field) : field)),
        formConfig: {
          ...config.formConfig,
          mode: 'all', // Validate on all events
          criteriaMode: 'all', // Show all validation errors
          shouldFocusError: true, // Focus on first error
          reValidateMode: 'onChange', // Re-validate on change
        },
        validateSteps: config.validateSteps ?? true,
        initialized: true,
      } as AnyFormStateT,
    }));
  };

  const resetForm = (formId: string) => {
    setFormStates(prev => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {[formId]: _, ...rest} = prev;
      return rest;
    });
  };

  // -------------- ----------------------------------------------
  // Helper Functions
  // ------------------------------------------------------------
  /**
   * Converts a simple watch configuration into the full watch config
   * @param field The form field with potential watch configuration
   * @returns The field with proper watch configuration
   */
  const processWatchConfig = <T extends object>(field: FormFieldT<T>): FormFieldT<T> => {
    // Only process fields with a watchConfig
    const {watchConfig} = field;
    if (!watchConfig) return field;

    const configs: WatchConfigT[] = [];

    // Process Copy Configurations
    if (watchConfig.copyFrom) {
      const copyConfig =
        typeof watchConfig.copyFrom === 'string' ? {source: watchConfig.copyFrom} : watchConfig.copyFrom;

      // If there's a disableWhen condition, only copy when that condition is met
      if (watchConfig.disableWhen) {
        configs.push({
          source: copyConfig.source,
          target: copyConfig.target || field.name.toString(),
          effect: ({value, target, setTarget, formContext}: WatchEffectPropsT) => {
            const disableField = watchConfig.disableWhen?.field;
            if (!disableField) return;
            const disableValue = formContext?.getValues(disableField);

            if (disableValue === watchConfig.disableWhen?.is && target) {
              setTarget(value, {shouldValidate: false, shouldDirty: true});
            }
          },
        });
      } else {
        // If no disableWhen, just do a straight copy
        configs.push({
          source: copyConfig.source,
          target: copyConfig.target || field.name.toString(),
          autoCopy: true,
        });
      }
    }

    // Process Disable Configuration
    if (watchConfig.disableWhen) {
      const disableWhen = watchConfig.disableWhen;

      configs.push({
        source: disableWhen.field,
        effect: ({value, setDisabled}: WatchEffectPropsT) => {
          setDisabled(value === disableWhen.is);
        },
      });
    }

    const processedField = {
      ...field,
      watch: {
        enabled: true,
        configs,
        ...field.watch,
      },
    };

    return processedField;
  };

  return (
    <AppFormContext.Provider
      value={{
        getFormState,
        setFormState,
        validateStep,
        nextStep,
        prevStep,
        goToStep,
        setGlobalError,
        setLoading,
        initForm,
        resetForm,
        getStepFields,
      }}
    >
      {children}
    </AppFormContext.Provider>
  );
};

/* ******************************************************************
 * Hook
 ****************************************************************** */
type UseAppFormPropsT<T extends FieldValues> = {
  formId: string;
  config?: {
    fields?: FormFieldT<T>[];
  } & Partial<FormStateT<T>>;
  enabled?: boolean;
};

export function useAppForm<T extends FieldValues>({
  formId,
  config,
  enabled = true,
}: UseAppFormPropsT<T>): FormHookReturnT<T> {
  const context = useContext(AppFormContext);
  const initialized = useRef(false);

  if (!context) {
    throw new Error('useAppForm must be used within a AppFormProvider');
  }

  // Calculate merged default values
  const mergedDefaultValues = useMemo(() => {
    if (!config) return undefined;

    // Get field defaults
    const fieldDefaults = config.fields?.reduce((acc, field) => ({...acc, [field.name]: field.defaultValue}), {});

    // Get passed-in defaults
    const passedDefaults = config.defaultValues || {};

    // Merge with field defaults, only overwriting if the passed value is explicitly set
    const merged = {
      ...fieldDefaults,
      ...Object.entries(passedDefaults).reduce(
        (acc, [key, value]) => {
          // Only override field defaults if the passed value is explicitly set
          if (value !== undefined && value !== '') {
            acc[key] = value;
          }
          return acc;
        },
        {} as Record<string, unknown>
      ),
    };

    return merged;
  }, [config]);

  // Initialize react-hook-form with merged defaults and optional Zod schema
  const formConfig = useForm<T>({
    defaultValues: mergedDefaultValues as DefaultValues<T>,
    ...(config?.zodSchema
      ? {
          resolver: zodResolver(config.zodSchema),
        }
      : null),
  });

  useEffect(() => {
    if (config && !initialized.current && enabled) {
      context.initForm<T>(formId, {...config, formConfig});
      initialized.current = true;
    }
  }, [formId, config, context, formConfig, enabled]);

  const state = context.getFormState<T>(formId);
  const boundMethods: FormHookReturnT<T> = {
    formConfig,
    state,
    setGlobalError: (error: string | null) => context.setGlobalError(formId, error),
    setLoading: (loading: boolean) => context.setLoading(formId, loading),
    getStepFields: () => context.getStepFields<T>(formId),
    validateStep: (step: number) => context.validateStep(formId, step),
    nextStep: () => context.nextStep(formId),
    prevStep: () => context.prevStep(formId),
    goToStep: (step: number) => context.goToStep(formId, step),
    resetForm: () => context.resetForm(formId),
  };

  return boundMethods;
}
